/**
 * RiderInfoDetail API
 * 라이더 상세정보 관련 API 호출
 */
import { apiClient } from '@/shared/api/client';
import {
    RiderApiItem,
    RiderAccountUpdateRequest,
    RiderDeductionApiResponse,
    RiderTypeListResponse,
    RiderTypeRawItem,
    RiderType,
    RiderTypeUpdateRequest
} from '../types/riderInfoDetail.types';

const BASE_PATH = '/api/v1/rider';

/**
 * 라이더 상세 정보 API
 */
export const riderInfoDetailApi = {
    /**
     * 라이더 상세 정보 조회
     * GET /api/v1/rider/detail
     */
    getRiderDetail: async (riderId: string): Promise<RiderApiItem> => {
        const response = await apiClient.get<RiderApiItem>(
            `${BASE_PATH}/detail`,
            { params: { riderId } }
        );
        return response.data;
    },

    /**
     * 라이더 계좌 정보 업데이트
     * POST /api/v1/rider/detail/account
     */
    updateRiderAccount: async (riderId: string, accountData: RiderAccountUpdateRequest): Promise<void> => {
        await apiClient.post<void>(
            `${BASE_PATH}/detail/account`,
            accountData
        );
    },

    /**
     * 라이더 공제 정보 조회
     * GET /api/v1/rider/deduct
     */
    getRiderDeductions: async (riderId: string, suppId: string): Promise<RiderDeductionApiResponse> => {
        if (!suppId) {
            throw new Error('suppId는 필수 파라미터입니다.');
        }

        const response = await apiClient.get<RiderDeductionApiResponse>(
            `${BASE_PATH}/deduct`,
            { params: { riderId, suppId } }
        );
        return response.data;
    },

    /**
     * 라이더 유형 목록 조회
     * GET /api/v1/rider/types
     */
    getRiderTypes: async (riderId?: string): Promise<RiderType[]> => {
        const params = riderId ? { riderId } : undefined;
        const response = await apiClient.get<RiderTypeListResponse>(`${BASE_PATH}/types`, { params });
        const data = response.data;

        const toItem = (item: RiderTypeRawItem): RiderType => ({
            code: (item.code ?? item.riderType ?? item.typeCode ?? '').toString(),
            name: item.name ?? item.riderTypeName ?? item.typeName ?? item.description ?? ''
        });

        let items: RiderTypeRawItem[] = [];

        if (Array.isArray(data)) {
            items = data;
        } else if (data) {
            if (Array.isArray((data as { riderTypes?: RiderTypeRawItem[] }).riderTypes)) {
                items = (data as { riderTypes: RiderTypeRawItem[] }).riderTypes;
            } else if (Array.isArray((data as { types?: RiderTypeRawItem[] }).types)) {
                items = (data as { types: RiderTypeRawItem[] }).types;
            } else if (Array.isArray((data as { items?: RiderTypeRawItem[] }).items)) {
                items = (data as { items: RiderTypeRawItem[] }).items;
            } else if (Array.isArray((data as { data?: RiderTypeRawItem[] }).data)) {
                items = (data as { data: RiderTypeRawItem[] }).data;
            }
        }

        return items
            .map(toItem)
            .filter((item) => item.code && item.name);
    },

    /**
     * 라이더 유형 업데이트
     * POST /api/v1/rider/detail/type
     */
    updateRiderType: async (request: RiderTypeUpdateRequest): Promise<void> => {
        await apiClient.post<void>(`${BASE_PATH}/detail/type`, request);
    }
};
